import { Box, Button, Chip, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { motion } from "framer-motion";
import { useState } from "react";
import env from "shared/config/env";
import { randomA } from "shared/palettes";
import JoinRoom from "./components/JoinRoom";
import useDominoSocket from "./hooks/useDominoSocket";

/* ── Player colors ─────────────────────────────────────── */
const ALL_P = {
  1: { label: "ผู้เล่น 1", color: "#1565c0", fill: "#2196f3", bg: "#e3f2fd" },
  2: { label: "ผู้เล่น 2", color: "#2e7d32", fill: "#4caf50", bg: "#e8f5e9" },
  3: { label: "ผู้เล่น 3", color: "#e65100", fill: "#ff9800", bg: "#fff3e0" },
  4: { label: "ผู้เล่น 4", color: "#6a1b9a", fill: "#ab47bc", bg: "#f3e5f5" },
  5: { label: "ผู้เล่น 5", color: "#00838f", fill: "#00bcd4", bg: "#e0f7fa" },
  6: { label: "ผู้เล่น 6", color: "#c62828", fill: "#ef5350", bg: "#ffebee" },
};

/* ── SVG domino tile ──────────────────────────────────── */
const PIP_POS = {
  0: [],
  1: [[50, 50]],
  2: [[25, 25], [75, 75]],
  3: [[25, 25], [50, 50], [75, 75]],
  4: [[25, 25], [75, 25], [25, 75], [75, 75]],
  5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
  6: [[25, 22], [75, 22], [25, 50], [75, 50], [25, 78], [75, 78]],
  7: [[25, 18], [75, 18], [25, 50], [75, 50], [25, 82], [75, 82], [50, 34]],
  8: [[25, 18], [75, 18], [25, 42], [75, 42], [25, 66], [75, 66], [25, 90], [75, 90]],
  9: [[25, 18], [75, 18], [25, 40], [75, 40], [25, 62], [75, 62], [25, 84], [75, 84], [50, 50]],
};

function DominoTile({ a, b, highlight, small, playerColor, onClick, faceDown }) {
  const W = small ? 32 : 52;
  const H = small ? 64 : 104;
  const R = small ? 2.5 : 4;
  const pipR = small ? 2.5 : 4;
  const divY = H / 2;

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        display: "block",
        flexShrink: 0,
        filter: highlight ? `drop-shadow(0 0 4px ${playerColor || "#1565c0"})` : "none",
        transition: "filter 0.2s",
      }}
    >
      <rect x={1} y={1} width={W - 2} height={H - 2} rx={R} fill={faceDown ? "#1a237e" : "white"} stroke={playerColor || "#555"} strokeWidth={highlight ? 2 : 1.5} />
      {!faceDown && (
        <>
          <line x1={4} y1={divY} x2={W - 4} y2={divY} stroke="#bbb" strokeWidth={1} />
          {PIP_POS[a]?.map(([px, py], i) => (
            <circle key={i} cx={(px / 100) * W} cy={(py / 100) * (H / 2)} r={pipR} fill="#222" />
          ))}
          {PIP_POS[b]?.map(([px, py], i) => (
            <circle key={i} cx={(px / 100) * W} cy={divY + (py / 100) * (H / 2)} r={pipR} fill="#222" />
          ))}
        </>
      )}
    </svg>
  );
}

const canPlay = (tile, leftEnd, rightEnd) =>
  tile.a === leftEnd || tile.b === leftEnd || tile.a === rightEnd || tile.b === rightEnd;

/* ── Lobby screen ─────────────────────────────────────── */
function LobbyScreen({ players, pending, onChange, isHost, onStart, socketRef, onExit }) {
  const handInfo = (n) => {
    const sz = n === 2 ? 15 : n <= 4 ? 10 : 7;
    return `แจกคนละ ${sz} ชิ้น • กองสำรอง ${55 - sz * n} ชิ้น`;
  };

  return (
    <Box sx={{ maxWidth: 460, mx: "auto", px: 2, py: 3 }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box sx={{ fontWeight: "bold", fontSize: "1.4em" }}>🎲 โดมิโน Double Nine</Box>
        <Box sx={{ fontSize: "0.82em", color: "#888", mt: 0.5 }}>รอผู้เล่น / ตั้งค่าเกม</Box>
      </Box>

      <Box sx={{ mb: 3, p: 2, background: "#f5f5f5", borderRadius: 2 }}>
        <Box sx={{ fontWeight: "bold", fontSize: "0.82em", color: "#555", mb: 1 }}>
          ผู้เล่นในห้อง ({players.length} คน)
        </Box>
        {players.length === 0 && <Box sx={{ fontSize: "0.8em", color: "#aaa" }}>ยังไม่มีผู้เล่น…</Box>}
        {players.map((p, i) => (
          <Box key={p.id} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Chip
              size="small"
              label={i === 0 ? "HOST" : `P${i + 1}`}
              sx={{ fontSize: "0.65em", bgcolor: i === 0 ? "#1b5e20" : "#e0e0e0", color: i === 0 ? "#fff" : "#555" }}
            />
            <Box sx={{ fontSize: "0.88em", fontWeight: p.id === socketRef.current?.id ? "bold" : "normal" }}>
              {p.name}{p.id === socketRef.current?.id && " (คุณ)"}
            </Box>
          </Box>
        ))}
      </Box>

      {isHost ? (
        <>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ fontWeight: "bold", fontSize: "0.85em", mb: 1, color: "#333" }}>
              จำนวนผู้เล่น{" "}
              {players.length < pending.numPlayers && (
                <Box component="span" sx={{ color: "#e65100", fontSize: "0.82em" }}>
                  (รอผู้เล่นอีก {pending.numPlayers - players.length} คน)
                </Box>
              )}
            </Box>
            <ToggleButtonGroup exclusive fullWidth size="small"
              value={pending.numPlayers}
              onChange={(_, v) => v && onChange({ ...pending, numPlayers: v })}
            >
              {[2, 3, 4, 5, 6].map((v) => (
                <ToggleButton key={v} value={v} sx={{ fontSize: "0.82em" }}>{v} คน</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Box sx={{ fontSize: "0.75em", color: "#999", mt: 0.5 }}>{handInfo(pending.numPlayers)}</Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Box sx={{ fontWeight: "bold", fontSize: "0.85em", mb: 1, color: "#333" }}>เป้าหมายคะแนน</Box>
            <ToggleButtonGroup exclusive fullWidth size="small"
              value={pending.targetScore}
              onChange={(_, v) => v && onChange({ ...pending, targetScore: v })}
            >
              {[100, 150, 200].map((v) => (
                <ToggleButton key={v} value={v} sx={{ fontSize: "0.82em" }}>{v} คะแนน</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={players.length < 2}
            onClick={() => onStart(pending.numPlayers, pending.targetScore)}
            sx={{ bgcolor: "#1b5e20" }}
          >
            {players.length < 2 ? "รอผู้เล่นอีกคน…" : "เริ่มเกม"}
          </Button>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 3, color: "#888", fontSize: "0.9em" }}>
          รอ host เริ่มเกม…
        </Box>
      )}

      <Box sx={{ textAlign: "center", mt: 2 }}>
        <Button size="small" variant="text" color="inherit" onClick={onExit}
          sx={{ fontSize: "0.72em", opacity: 0.45 }}>
          ออกจากห้อง
        </Button>
      </Box>
    </Box>
  );
}

/* ── Round result summary ─────────────────────────────── */
function RoundResult({ gs, players, myPlayerNum, isHost, onNextRound, onBackToLobby }) {
  const { scores, targetScore, gameOver, blocked, numPlayers } = gs;
  const winnerP = gameOver
    ? Number(Object.keys(scores).reduce((a, b) => scores[a] > scores[b] ? a : b))
    : null;
  const winnerName = winnerP && players[winnerP - 1]?.name ? players[winnerP - 1].name : `ผู้เล่น ${winnerP}`;

  return (
    <Box sx={{ textAlign: "center", py: 2, px: 2, maxWidth: 460, mx: "auto" }}>
      <Box sx={{ fontSize: "1.4em", fontWeight: "bold", mb: 1 }}>
        {gameOver ? `🏆 ${winnerName} ชนะเกม!` : blocked ? "🔒 เกมตัน" : "✅ จบรอบ"}
      </Box>
      <Box sx={{ fontSize: "0.82em", color: "#777", mb: 2 }}>
        {gameOver ? `ถึง ${targetScore} คะแนนแล้ว` : "ผลคะแนนสะสม"}
      </Box>
      <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 2 }}>
        {Array.from({ length: numPlayers }, (_, i) => i + 1).map((p) => {
          const name = players[p - 1]?.name || `ผู้เล่น ${p}`;
          return (
            <Box key={p} sx={{ px: 1.5, py: 0.75, border: `2px solid ${ALL_P[p].fill}`, borderRadius: 2, minWidth: 70, textAlign: "center" }}>
              <Box sx={{ fontWeight: "bold", fontSize: "0.78em", color: ALL_P[p].color }}>
                {name}{p === myPlayerNum && " (คุณ)"}
              </Box>
              <Box sx={{ fontWeight: "bold" }}>{scores[p] || 0} คะแนน</Box>
            </Box>
          );
        })}
      </Stack>
      {isHost ? (
        <Stack direction="row" spacing={1} justifyContent="center">
          {!gameOver && <Button variant="contained" color="primary" onClick={onNextRound}>รอบต่อไป</Button>}
          <Button variant="outlined" color="inherit" onClick={onBackToLobby}>ตั้งค่าใหม่</Button>
        </Stack>
      ) : (
        <Box sx={{ fontSize: "0.82em", color: "#888" }}>รอ host เริ่มต่อ…</Box>
      )}
    </Box>
  );
}

/* ── Main game screen ─────────────────────────────────── */
function GameScreen({ gs, myHand, myPlayerNum, players, isHost, socketActions }) {
  const { chain, leftEnd, rightEnd, cur, handCounts, boneyardCount, numPlayers, msg } = gs;
  const { placeTile, drawBoneyard, passTurn, backToLobby, exitRoom } = socketActions;
  const isMyTurn = myPlayerNum > 0 && cur === myPlayerNum;
  const P = ALL_P;

  const playableTiles = chain.length === 0
    ? myHand
    : myHand.filter((t) => canPlay(t, leftEnd, rightEnd));
  const canPlayAny = chain.length === 0 || playableTiles.length > 0;

  const playerName = (p) => players[p - 1]?.name || `ผู้เล่น ${p}`;

  return (
    <Box sx={{ maxWidth: 520, mx: "auto", px: 2, py: 2 }}>
      <Box sx={{ textAlign: "center", mb: 1.5 }}>
        <Box sx={{ fontWeight: "bold", fontSize: "1.15em" }}>🎲 โดมิโน Double Nine</Box>
        {myPlayerNum > 0 && (
          <Box sx={{ fontSize: "0.78em", color: P[myPlayerNum]?.color, fontWeight: "bold", mt: 0.25 }}>
            คุณ = {playerName(myPlayerNum)}
          </Box>
        )}
      </Box>

      {/* Score row */}
      <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: "wrap", gap: 0.75 }}>
        {Array.from({ length: numPlayers }, (_, i) => i + 1).map((p) => (
          <Box key={p} sx={{
            flex: "1 1 60px", textAlign: "center", py: 0.75, px: 0.5, borderRadius: 2,
            border: `2px solid ${P[p].fill}`,
            background: cur === p ? P[p].bg : "transparent",
            transition: "background 0.3s",
            minWidth: 60,
          }}>
            <Box sx={{ fontWeight: "bold", fontSize: "0.72em", color: P[p].color }}>
              {cur === p ? "▶ " : ""}{playerName(p)}{p === myPlayerNum ? " (คุณ)" : ""}
            </Box>
            <Box sx={{ fontSize: "0.9em", fontWeight: "bold" }}>{gs.scores[p] || 0}</Box>
            <Box sx={{ fontSize: "0.65em", color: "#999" }}>{handCounts[p] || 0} ชิ้น</Box>
          </Box>
        ))}
      </Stack>

      {/* Chain board */}
      <Box sx={{ mb: 1.5, background: "#1b5e20", borderRadius: 2, p: 1, minHeight: 80, display: "flex", alignItems: "center", overflowX: "auto" }}>
        {chain.length === 0 ? (
          <Box sx={{ color: "#a5d6a7", fontSize: "0.8em", mx: "auto" }}>— วางกระเบื้องแรกเพื่อเริ่ม —</Box>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mx: "auto", px: 1 }}>
            <Box sx={{ color: "#fff", fontWeight: "bold", fontSize: "0.78em", mr: 0.5 }}>[{leftEnd}]</Box>
            {chain.map((tile, i) => (
              <DominoTile key={`${tile.id}-${i}`} a={tile.a} b={tile.b} small />
            ))}
            <Box sx={{ color: "#fff", fontWeight: "bold", fontSize: "0.78em", ml: 0.5 }}>[{rightEnd}]</Box>
          </Stack>
        )}
      </Box>

      <Box sx={{ textAlign: "center", fontSize: "0.72em", color: "#777", mb: 1 }}>
        กองสำรอง: {boneyardCount} ชิ้น • เป้า: {gs.targetScore} คะแนน
      </Box>

      {msg && (
        <Box sx={{ textAlign: "center", fontSize: "0.82em", mb: 1, color: msg.startsWith("🎉") || msg.startsWith("🔒") ? "#2e7d32" : "#e65100", fontWeight: msg.startsWith("🎉") ? "bold" : "normal" }}>
          {msg}
        </Box>
      )}

      {/* My hand */}
      {myPlayerNum > 0 && (
        <Box sx={{ border: `2px solid ${P[myPlayerNum].fill}`, borderRadius: 2, p: 1.5, mb: 1.5, background: P[myPlayerNum].bg }}>
          <Box sx={{ fontWeight: "bold", fontSize: "0.85em", color: P[myPlayerNum].color, mb: 1 }}>
            มือของคุณ ({myHand.length} ชิ้น){isMyTurn ? " — ตาคุณ!" : ""}
          </Box>

          {isMyTurn ? (
            <Box>
              {chain.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ fontSize: "0.7em", color: "#666", mb: 0.5 }}>วางต่อด้านซ้าย [{leftEnd}]</Box>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {myHand.filter((t) => t.a === leftEnd || t.b === leftEnd).map((tile) => (
                      <DominoTile key={tile.id} a={tile.a} b={tile.b} highlight playerColor={P[myPlayerNum].fill}
                        onClick={() => placeTile(tile.id, "left")} />
                    ))}
                    {myHand.filter((t) => t.a === leftEnd || t.b === leftEnd).length === 0 && (
                      <Box sx={{ fontSize: "0.72em", color: "#aaa" }}>ไม่มี</Box>
                    )}
                  </Stack>
                </Box>
              )}
              {chain.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ fontSize: "0.7em", color: "#666", mb: 0.5 }}>วางต่อด้านขวา [{rightEnd}]</Box>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {myHand.filter((t) => t.a === rightEnd || t.b === rightEnd).map((tile) => (
                      <DominoTile key={tile.id} a={tile.a} b={tile.b} highlight playerColor={P[myPlayerNum].fill}
                        onClick={() => placeTile(tile.id, "right")} />
                    ))}
                    {myHand.filter((t) => t.a === rightEnd || t.b === rightEnd).length === 0 && (
                      <Box sx={{ fontSize: "0.72em", color: "#aaa" }}>ไม่มี</Box>
                    )}
                  </Stack>
                </Box>
              )}
              {chain.length === 0 && (
                <Box>
                  <Box sx={{ fontSize: "0.7em", color: "#666", mb: 0.5 }}>เลือกกระเบื้องวางแรก</Box>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {myHand.map((tile) => (
                      <DominoTile key={tile.id} a={tile.a} b={tile.b} highlight playerColor={P[myPlayerNum].fill}
                        onClick={() => placeTile(tile.id, "right")} />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          ) : (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
              {myHand.map((tile) => (
                <DominoTile key={tile.id} a={tile.a} b={tile.b} small />
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Action buttons */}
      {isMyTurn && (
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1.5 }}>
          {!canPlayAny && boneyardCount > 0 && (
            <Button variant="contained" color="warning" onClick={drawBoneyard}>
              หยิบจากกองสำรอง ({boneyardCount})
            </Button>
          )}
          {!canPlayAny && boneyardCount === 0 && (
            <Button variant="outlined" color="inherit" onClick={passTurn}>
              ข้ามเทิร์น (Pass)
            </Button>
          )}
        </Stack>
      )}

      {!isMyTurn && myPlayerNum > 0 && (
        <Box sx={{ textAlign: "center", fontSize: "0.85em", color: P[cur]?.color, fontWeight: "bold", mb: 1.5 }}>
          รอ {playerName(cur)}…
        </Box>
      )}

      {isHost && (
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <Button size="small" variant="text" color="inherit" onClick={backToLobby}
            sx={{ fontSize: "0.72em", opacity: 0.45 }}>
            ตั้งค่า / เริ่มใหม่
          </Button>
        </Box>
      )}

      <Box sx={{ textAlign: "center", mt: 0.5 }}>
        <Button size="small" variant="text" color="inherit" onClick={exitRoom}
          sx={{ fontSize: "0.72em", opacity: 0.35 }}>
          ออกจากห้อง
        </Button>
      </Box>

      <Box sx={{ mt: 2, p: 1.5, background: "#f5f5f5", borderRadius: 2, fontSize: "0.73em", color: "#666", lineHeight: 1.7 }}>
        <Box sx={{ fontWeight: "bold", mb: 0.5, color: "#333" }}>กติกา (Double Nine)</Box>
        <Box>• ผลัดกันวางกระเบื้องต่อปลายเปิดของสาย</Box>
        <Box>• จุดบนกระเบื้องต้องตรงกับปลายเปิด</Box>
        <Box>• วางไม่ได้ → หยิบจากกองสำรอง → ถ้าหมดให้ Pass</Box>
        <Box>• วางหมดก่อน → ได้คะแนนเท่าจุดที่เหลือในมือผู้อื่น</Box>
        <Box>• เกมตัน → ผู้มีจุดน้อยสุดชนะรอบ</Box>
      </Box>
    </Box>
  );
}

/* ── Root component ───────────────────────────────────── */
const DEFAULT_SETTINGS = { numPlayers: 2, targetScore: 100 };

export default function Domino({ duration }) {
  const [inputCode, setInputCode] = useState(env.defaultRoomCode);
  const [userName, setUserName] = useState(
    () => randomA[Math.floor(Math.random() * randomA.length)]
  );
  const [pending, setPending] = useState(DEFAULT_SETTINGS);

  const {
    isJoined, players, gameStarted, myHand, myPlayerNum, gs,
    socketRef, joinRoom, startGame, placeTile, drawBoneyard, passTurn, nextRound, backToLobby, exitRoom,
  } = useDominoSocket(inputCode, userName);

  const isHost = players[0]?.id === socketRef.current?.id;

  const handleJoinRoom = () => { if (inputCode) joinRoom(); };

  const wrap = (children) => (
    <motion.div
      initial={{ x: -10, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 10, opacity: 0 }}
      transition={{ duration }}
    >
      {children}
    </motion.div>
  );

  if (!isJoined) {
    return wrap(
      <JoinRoom
        inputCode={inputCode}
        setInputCode={setInputCode}
        userName={userName}
        setUserName={setUserName}
        handleJoinRoom={handleJoinRoom}
      />
    );
  }

  if (!gameStarted) {
    return wrap(
      <LobbyScreen
        players={players}
        pending={pending}
        onChange={setPending}
        isHost={isHost}
        onStart={startGame}
        socketRef={socketRef}
        onExit={exitRoom}
      />
    );
  }

  if (gs?.roundOver || gs?.gameOver) {
    return wrap(
      <RoundResult
        gs={gs}
        players={players}
        myPlayerNum={myPlayerNum}
        isHost={isHost}
        onNextRound={nextRound}
        onBackToLobby={backToLobby}
      />
    );
  }

  return wrap(
    <GameScreen
      gs={gs}
      myHand={myHand}
      myPlayerNum={myPlayerNum}
      players={players}
      isHost={isHost}
      socketActions={{ placeTile, drawBoneyard, passTurn, backToLobby, exitRoom }}
    />
  );
}
